import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Select,
  Option,
  Divider,
  FormControl,
} from '@mui/joy';
import {
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
  ArrowRight as TrendingFlat,
  DownloadSimple as Download,
  Printer as Print,
  ArrowsClockwise as Refresh,
  Gauge as Speed,
  CurrencyDollar as AttachMoney,
  Calendar as CalendarToday,
} from '@phosphor-icons/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { apiAccountingClient } from '../../lib/apiAccounting';
import type { 
  FinancialAnalyticsData, 
  FinancialRatio,
  HealthStatus,
  TrendDirection 
} from '../../types/accounting';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatPercent = (value: number) => {
  return `${value.toFixed(1)}%`;
};

const getHealthColor = (health: HealthStatus): 'success' | 'warning' | 'danger' => {
  switch (health) {
    case 'good': return 'success';
    case 'warning': return 'warning';
    case 'poor': return 'danger';
    default: return 'warning';
  }
};

const getTrendIcon = (trend?: TrendDirection) => {
  switch (trend) {
    case 'up': return <TrendingUp size={16} />;
    case 'down': return <TrendingDown size={16} />;
    case 'stable': return <TrendingFlat size={16} />;
    default: return null;
  }
};

// KPI Card Component
interface KPICardProps {
  title: string;
  value: number;
  format: 'currency' | 'percent' | 'number';
  change?: number;
  trend?: TrendDirection;
  health?: HealthStatus;
  icon?: React.ReactNode;
  description?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  format,
  change,
  trend,
  health,
  icon,
  description,
}) => {
  const formatValue = () => {
    switch (format) {
      case 'currency': return formatCurrency(value);
      case 'percent': return formatPercent(value);
      default: return value.toFixed(2);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            {title}
          </Typography>
          {icon}
        </Box>
        
        <Typography level="h3" sx={{ mb: 1 }}>
          {formatValue()}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {trend && change !== undefined && (
            <Chip
              size="sm"
              color={trend === 'up' ? 'success' : trend === 'down' ? 'danger' : 'neutral'}
              startDecorator={getTrendIcon(trend)}
            >
              {change > 0 ? '+' : ''}{change.toFixed(1)}%
            </Chip>
          )}
          
          {health && (
            <Chip
              size="sm"
              color={getHealthColor(health)}
              variant="soft"
            >
              {health}
            </Chip>
          )}
        </Box>
        
        {description && (
          <Typography level="body-xs" sx={{ mt: 1, color: 'text.secondary' }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Ratio Card Component with visual gauge
const RatioCard: React.FC<{ ratio: FinancialRatio & { isPlaceholder?: boolean } }> = ({ ratio }) => {
  const isPercentage = ratio.unit === '%';
  const isPlaceholder = ratio.isPlaceholder;

  // Calculate progress towards benchmark (capped at 150% for visual)
  const benchmarkVal = ratio.benchmark || 1;
  const progressRaw = isPlaceholder ? 0 : Math.abs(ratio.value) / Math.abs(benchmarkVal) * 100;
  const progress = Math.min(progressRaw, 150);
  const healthColor = isPlaceholder ? 'neutral' as const : getHealthColor(ratio.health);

  return (
    <Box sx={{
      p: 2,
      borderRadius: 'sm',
      bgcolor: 'background.level1',
      height: '100%',
      opacity: isPlaceholder ? 0.5 : 1,
      border: isPlaceholder ? '1px dashed' : 'none',
      borderColor: 'neutral.outlinedBorder',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography level="body-sm" fontWeight="md">{ratio.name}</Typography>
        {isPlaceholder ? (
          <Chip size="sm" color="neutral" variant="outlined">
            N/A
          </Chip>
        ) : (
          <Chip size="sm" color={healthColor} variant="soft">
            {ratio.health}
          </Chip>
        )}
      </Box>

      <Typography level="h3" sx={{ mb: 1 }}>
        {isPlaceholder ? '—' : (isPercentage ? formatPercent(ratio.value) : ratio.value.toFixed(2))}
      </Typography>

      {/* Visual gauge bar */}
      {ratio.benchmark !== undefined && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{
            width: '100%',
            height: 6,
            borderRadius: 3,
            bgcolor: 'background.level2',
            overflow: 'hidden',
          }}>
            <Box sx={{
              width: `${Math.min(progress, 100)}%`,
              height: '100%',
              borderRadius: 3,
              bgcolor: isPlaceholder ? 'neutral.400' : (healthColor === 'success' ? 'success.500' : healthColor === 'warning' ? 'warning.500' : 'danger.500'),
              transition: 'width 0.5s ease',
            }} />
          </Box>
          <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Benchmark: {isPercentage ? formatPercent(ratio.benchmark) : ratio.benchmark}
          </Typography>
        </Box>
      )}

      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
        {isPlaceholder ? 'Insufficient data to calculate' : ratio.description}
      </Typography>
    </Box>
  );
};

export const FinancialAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<FinancialAnalyticsData | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('current');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const endDate = selectedPeriod === 'current' 
        ? undefined 
        : new Date(new Date().setMonth(new Date().getMonth() - parseInt(selectedPeriod))).toISOString().split('T')[0];
      
      const response = await apiAccountingClient.getFinancialAnalytics(
        endDate ? { end_date: endDate } : undefined
      );
      setAnalyticsData(response);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load financial analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export analytics');
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !analyticsData) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        {error || 'Failed to load analytics data'}
      </Alert>
    );
  }

  // Prepare chart data
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // All possible ratios with placeholders for ones that can't be calculated
  const allRatioDefinitions: FinancialRatio[] = [
    // Liquidity
    { name: 'Current Ratio', value: 0, category: 'liquidity', benchmark: 2.0, health: 'neutral' as HealthStatus, description: 'Current assets / current liabilities' },
    { name: 'Quick Ratio', value: 0, category: 'liquidity', benchmark: 1.0, health: 'neutral' as HealthStatus, description: 'Liquid assets / current liabilities' },
    { name: 'Cash Ratio', value: 0, category: 'liquidity', benchmark: 0.2, health: 'neutral' as HealthStatus, description: 'Cash / current liabilities' },
    // Profitability
    { name: 'Gross Margin', value: 0, category: 'profitability', benchmark: 30, health: 'neutral' as HealthStatus, description: 'Gross profit as % of revenue', unit: '%' },
    { name: 'Net Margin (YTD)', value: 0, category: 'profitability', benchmark: 10, health: 'neutral' as HealthStatus, description: 'Net profit as % of revenue', unit: '%' },
    { name: 'Return on Assets', value: 0, category: 'profitability', benchmark: 5, health: 'neutral' as HealthStatus, description: 'How efficiently assets generate profit', unit: '%' },
    { name: 'Return on Equity', value: 0, category: 'profitability', benchmark: 15, health: 'neutral' as HealthStatus, description: 'Return generated on equity', unit: '%' },
    // Leverage
    { name: 'Debt to Equity', value: 0, category: 'leverage', benchmark: 1.0, health: 'neutral' as HealthStatus, description: 'Financial leverage ratio' },
    { name: 'Debt Ratio', value: 0, category: 'leverage', benchmark: 40, health: 'neutral' as HealthStatus, description: 'Percentage of assets financed by debt', unit: '%' },
    // Efficiency
    { name: 'Asset Turnover', value: 0, category: 'efficiency', benchmark: 1.0, health: 'neutral' as HealthStatus, description: 'Revenue generated per dollar of assets' },
  ];

  // Merge actual ratios with placeholders
  const mergedRatios = allRatioDefinitions.map(placeholder => {
    const actual = analyticsData.ratios.find(r => r.name === placeholder.name);
    if (actual) return actual;
    return { ...placeholder, isPlaceholder: true };
  });

  // Group ratios by category
  const ratiosByCategory = mergedRatios.reduce((acc, ratio) => {
    if (!acc[ratio.category]) acc[ratio.category] = [];
    acc[ratio.category].push(ratio);
    return acc;
  }, {} as Record<string, (FinancialRatio & { isPlaceholder?: boolean })[]>);

  return (
    <Box className="financial-analytics">
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 1.5, mb: 3 }}>
        <Box>
          <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined } }}>
            Financial Analytics
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            {analyticsData.period.current_month} • As of {new Date(analyticsData.period.as_of_date).toLocaleDateString()}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 calc(50% - 4px)', md: '0 0 auto' } } }}>
          <FormControl size="sm">
            <Select
              value={selectedPeriod}
              onChange={(_, value) => value && setSelectedPeriod(value)}
              startDecorator={<CalendarToday />}
            >
              <Option value="current">Current Month</Option>
              <Option value="1">Last Month</Option>
              <Option value="3">3 Months Ago</Option>
              <Option value="6">6 Months Ago</Option>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Refresh />}
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refresh
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Download />}
            onClick={handleExport}
          >
            Export
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Print />}
            onClick={handlePrint}
          >
            Print
          </Button>
        </Stack>
      </Box>

      {/* Key KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {analyticsData.kpis.map((kpi, index) => (
          <Grid key={index} xs={12} sm={6} md={3}>
            <KPICard
              title={kpi.name}
              value={kpi.value}
              format="currency"
              change={kpi.change}
              trend={kpi.trend}
              health={kpi.health}
              icon={<AttachMoney />}
            />
          </Grid>
        ))}
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Revenue vs Expenses Trend */}
        <Grid xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Revenue vs Expenses Trend
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analyticsData.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value: any) => value !== undefined ? formatCurrency(Number(value) || 0) : '$0.00'} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stackId="1"
                    stroke="#0088FE" 
                    fill="#0088FE"
                    fillOpacity={0.6}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="expenses" 
                    stackId="2"
                    stroke="#FF8042" 
                    fill="#FF8042"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Metrics */}
        <Grid xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Financial Position
              </Typography>
              
              <Stack spacing={2}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Assets
                  </Typography>
                  <Typography level="h4">
                    {formatCurrency(analyticsData.summary.total_assets)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Liabilities
                  </Typography>
                  <Typography level="h4">
                    {formatCurrency(analyticsData.summary.total_liabilities)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Equity
                  </Typography>
                  <Typography level="h4">
                    {formatCurrency(analyticsData.summary.total_equity)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Net Income (YTD)
                  </Typography>
                  <Typography 
                    level="h4"
                    sx={{ 
                      color: analyticsData.summary.net_income_ytd >= 0 ? 'success.main' : 'danger.main' 
                    }}
                  >
                    {formatCurrency(analyticsData.summary.net_income_ytd)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Ratios */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Financial Ratios
          </Typography>
          
          {Object.entries(ratiosByCategory).map(([category, ratios]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography 
                level="title-md" 
                sx={{ 
                  mb: 2, 
                  textTransform: 'capitalize',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Speed /> {category} Ratios
              </Typography>
              
              <Grid container spacing={2}>
                {ratios.map((ratio, index) => (
                  <Grid key={index} xs={12} sm={6} md={4}>
                    <RatioCard ratio={ratio} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Profit Trend Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Monthly Profit Trend
          </Typography>
          
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={analyticsData.trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: any) => value !== undefined ? formatCurrency(Number(value) || 0) : '$0.00'} />
              <Legend />
              <Bar 
                dataKey="profit" 
                fill="#00C49F"
                name="Net Profit"
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Print Styles */}
      <style>{`
        @media print {
          .financial-analytics {
            padding: 20px;
          }
          button, .MuiButton-root {
            display: none !important;
          }
          .MuiSelect-root {
            display: none !important;
          }
        }
      `}</style>
    </Box>
  );
};